<template>
  <Modal
    :show="true"
    :title="isEditing ? $t('notes.editNote') : $t('notes.createNote')"
    size="large"
    @close="$emit('close')"
    @cancel="$emit('close')"
  >    <form @submit.prevent="handleSubmit">
        <!-- Title -->
        <div class="app-form-field">
          <label class="app-form-field__label app-form-field__label--required">
            <svg viewBox="0 0 24 24" width="18" height="18" class="app-form-field__icon">
              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
            </svg>
            {{ $t('notes.title') }}
          </label>
          <div class="app-form-field__wrapper">
            <input
              id="noteTitle"
              v-model="formData.title"
              type="text"
              class="app-form-field__input"
              :class="{ 'app-form-field__input--error': errors.title }"
              :placeholder="$t('notes.titlePlaceholder')"
              required
              maxlength="255"
            />
          </div>
          <div v-if="errors.title" class="app-form-field__error">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"/>
            </svg>
            {{ errors.title }}
          </div>
        </div>

        <!-- Content -->
        <div class="app-form-field">
          <label class="app-form-field__label app-form-field__label--required">
            <svg viewBox="0 0 24 24" width="18" height="18" class="app-form-field__icon">
              <path fill="currentColor" d="M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"/>
            </svg>
            {{ $t('notes.content') }}
          </label>
          <div class="app-form-field__wrapper">
            <textarea
              id="noteContent"
              v-model="formData.content"
              class="app-form-field__input app-form-field__input--textarea"
              :class="{ 'app-form-field__input--error': errors.content }"
              :placeholder="$t('notes.contentPlaceholder')"
              required
              rows="6"
              maxlength="2000"
            ></textarea>
          </div>
          <div class="app-form-field__meta">
            <div v-if="errors.content" class="app-form-field__error">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"/>
              </svg>
              {{ errors.content }}
            </div>
            <div class="app-form-field__char-count" :class="{ 'app-form-field__char-count--warning': formData.content.length > 1800 }">
              {{ formData.content.length }}/2000
            </div>
          </div>
        </div>

        <!-- Priority -->
        <div class="app-form-field">
          <label class="app-form-field__label app-form-field__label--required">
            <svg viewBox="0 0 24 24" width="18" height="18" class="app-form-field__icon">
              <path fill="currentColor" d="M14,19H18V17H16V16H17A1,1 0 0,0 18,15V14A1,1 0 0,0 17,13H15V15H17V16H15A1,1 0 0,0 14,17V18A1,1 0 0,0 15,19H14M6,19H10V17H8V16H9A1,1 0 0,0 10,15V14A1,1 0 0,0 9,13H7V15H9V16H7A1,1 0 0,0 6,17V18A1,1 0 0,0 7,19H6M12,19H13V13H11V15H12V19Z"/>
            </svg>
            {{ $t('notes.priority.label') }}
          </label>
          <div class="app-form-field__wrapper">
            <select
              id="notePriority"
              v-model="formData.priority"
              class="app-form-field__input app-form-field__input--select"
              :class="{ 'app-form-field__input--error': errors.priority }"
              required
            >
              <option value="">{{ $t('notes.selectPriority') }}</option>
              <option value="LOW">{{ $t('notes.priority.low') }}</option>
              <option value="NORMAL">{{ $t('notes.priority.normal') }}</option>
              <option value="HIGH">{{ $t('notes.priority.high') }}</option>
              <option value="CRITICAL">{{ $t('notes.priority.critical') }}</option>
            </select>
          </div>
          <div v-if="errors.priority" class="app-form-field__error">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"/>
            </svg>
            {{ errors.priority }}
          </div>
        </div>

        <!-- Error message -->
        <div v-if="submitError" class="note-form__error">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="currentColor" d="M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"/>
          </svg>
          {{ submitError }}
        </div>
      </form>

      <template #footer>
        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn btn-secondary">
            {{ $t('common.cancel') }}
          </button>
          <button 
            type="submit" 
            class="btn btn-primary" 
            :disabled="loading || !isFormValid"
            @click="handleSubmit"
          >
            <span v-if="loading" class="loading-spinner"></span>
            {{ loading ? $t('common.saving') : (isEditing ? $t('common.update') : $t('common.create')) }}
          </button>
        </div>
      </template>
    </Modal>
  </template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, reactive, computed, watch } from 'vue';
// @ts-ignore
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/components/base/Modal.vue';
import type { Note } from '@/types/services';

interface Props {
  note: Note | null;
  projectId: string | number;
}

interface Emits {
  (e: 'save', noteData: NoteData): void;
  (e: 'close'): void;
}

interface NoteData {
  title: string;
  content: string;
  priority: string;
  projectId: string | number;
}

interface FormErrors {
  title: string;
  content: string;
  priority: string;
}

const props = withDefaults(defineProps<Props>(), {
  note: null
});

const emit = defineEmits<Emits>();

const { t } = useI18n();

// Form data
const formData = reactive<NoteData>({
  title: '',
  content: '',
  priority: '',
  projectId: props.projectId
});

// Form state
const loading = ref<boolean>(false);
const submitError = ref<string>('');

const errors = reactive<FormErrors>({
  title: '',
  content: '',
  priority: ''
});

// Computed properties
const isEditing = computed<boolean>(() => !!props.note);

const isFormValid = computed<boolean>(() => {
  return !!(formData.title.trim() &&
         formData.content.trim() &&
         formData.priority &&
         !Object.values(errors).some((error: any) => error.length > 0));
});// Validation functions
const validateTitle = (): void => {
  if (!formData.title.trim()) {
    errors.title = t('notes.validation.titleRequired');
  } else if (formData.title.length > 255) {
    errors.title = t('notes.validation.titleTooLong');
  } else {
    errors.title = '';
  }
};

const validateContent = (): void => {
  if (!formData.content.trim()) {
    errors.content = t('notes.validation.contentRequired');
  } else if (formData.content.length > 2000) {
    errors.content = t('notes.validation.contentTooLong');
  } else {
    errors.content = '';
  }
};

const validatePriority = (): void => {
  if (!formData.priority) {
    errors.priority = t('notes.validation.priorityRequired');
  } else {
    errors.priority = '';
  }
};

const validateForm = (): void => {
  validateTitle();
  validateContent();
  validatePriority();
};

// Watch for form changes to validate
watch(() => formData.title, validateTitle);
watch(() => formData.content, validateContent);
watch(() => formData.priority, validatePriority);

// Handle form submission
const handleSubmit = async (): Promise<void> => {
  validateForm();

  if (!isFormValid.value) {
    return;
  }

  try {
    loading.value = true;
    submitError.value = '';

    const noteData: NoteData = {
      title: formData.title.trim(),
      content: formData.content.trim(),
      priority: formData.priority,
      projectId: props.projectId
    };

    emit('save', noteData);

  } catch (error: any) {
    console.error('Error saving note:', error);
    submitError.value = error.message || t('notes.errorSaving');
  } finally {
    loading.value = false;
  }
};

// Initialize form data when editing
const initializeForm = (): void => {
  if (props.note) {
    formData.title = props.note.title || '';
    formData.content = props.note.content || '';
    formData.priority = props.note.priority || '';
  } else {
    // Reset form for new note
    formData.title = '';
    formData.content = '';
    formData.priority = '';
  }

  // Clear errors
  Object.keys(errors).forEach(key => {
    (errors as any)[key] = '';
  });

  submitError.value = '';
};

// Watch for prop changes
watch(() => props.note, initializeForm, { immediate: true });

onMounted(() => {
  initializeForm();
  // Focus on title input
  setTimeout(() => {
    const titleInput = document.getElementById('noteTitle') as HTMLInputElement;
    if (titleInput) {
      titleInput.focus();
    }
  }, 100);
});
</script>

<style scoped>
/* ==========================================================================
   FORM FIELDS - Design System
   ========================================================================== */

.app-form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-bottom: var(--space-6);
}

.app-form-field__label {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--neutral-700);
  margin: 0;
}

.app-form-field__label--required::after {
  content: ' *';
  color: var(--error);
  font-weight: var(--font-weight-bold);
}

.app-form-field__icon {
  color: var(--primary);
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.app-form-field__wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.app-form-field__input {
  width: 100%;
  padding: var(--space-4);
  border: 2px solid var(--neutral-200);
  border-radius: var(--radius-base);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  background: white;
  transition: all var(--transition-base) ease;
  outline: none;
  position: relative;
  z-index: 1;
  color: var(--neutral-700);
}

.app-form-field__input--textarea {
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
  line-height: 1.6;
}

.app-form-field__input--select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right var(--space-2) center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: var(--space-8);
}

.app-form-field__input:focus {
  border-color: var(--primary);
  background: white;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  transform: translateY(-1px);
}

.app-form-field__input--error {
  border-color: var(--error);
  background: rgba(239, 68, 68, 0.05);
}

.app-form-field__input--error:focus {
  border-color: var(--error);
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.app-form-field__error {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--error);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  margin-top: calc(var(--space-1) * -1);
}

.app-form-field__error svg {
  flex-shrink: 0;
}

.app-form-field__meta {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--space-4);
}

.app-form-field__char-count {
  font-size: var(--font-size-xs);
  color: var(--neutral-500);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

.app-form-field__char-count--warning {
  color: var(--warning);
  font-weight: var(--font-weight-semibold);
}

/* General Error */
.note-form__error {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-6);
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: var(--radius-base);
  color: var(--error);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-6);
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-4);
  padding-top: var(--space-6);
  border-top: 1px solid var(--neutral-200);
}

.btn {
  padding: var(--space-2) var(--space-6);
  border: none;
  border-radius: var(--radius-base);
  cursor: pointer;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
  transition: all var(--transition-base) ease;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  color: white;
  box-shadow: var(--shadow-lg);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow-xl);
}

.btn-primary:active {
  transform: translateY(0);
}

.btn-secondary {
  background: var(--neutral-100);
  color: var(--neutral-700);
  border: 1px solid var(--neutral-300);
}

.btn-secondary:hover {
  background: var(--neutral-200);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-footer {
    flex-direction: column-reverse;
    gap: var(--space-2);
  }
  
  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
